import { useState, FormEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ingestWearable, ingestLab, ingestSymptom, ingestMedication,
  ingestContext, ingestAtom,
} from '../lib/api'

type AtomCategory = 'wearable' | 'lab' | 'symptom' | 'medication' | 'context' | 'generic'

const ATOM_CONFIG: Record<string, { category: AtomCategory; template: Record<string, unknown> }> = {
  SleepAtom:      { category: 'wearable', template: { atom_type: 'SleepAtom',      payload: { duration_h: 7.5, quality_score: 80, rem_pct: 25 } } },
  HeartRateAtom:  { category: 'wearable', template: { atom_type: 'HeartRateAtom',  payload: { bpm_avg: 65, bpm_max: 140, bpm_resting: 58 } } },
  HRVAtom:        { category: 'wearable', template: { atom_type: 'HRVAtom',        payload: { rmssd: 45, sdnn: 50 } } },
  ActivityAtom:   { category: 'wearable', template: { atom_type: 'ActivityAtom',   payload: { steps: 8000, active_minutes: 45, vo2max_estimate: 42 } } },
  GaitAtom:       { category: 'wearable', template: { atom_type: 'GaitAtom',       payload: { gait_variability_score: 0.12, cadence_spm: 108 } } },
  BiomarkerAtom:  { category: 'lab',      template: { name: 'ferritin', value: 35, unit: 'ng/mL', ref_range: { low: 30, high: 300 } } },
  SymptomAtom:    { category: 'symptom',  template: { symptom: 'fatigue', severity: 3, duration_days: 2 } },
  NutrientAtom:   { category: 'generic',  template: { atom_type: 'NutrientAtom',   payload: { name: 'iron', amount_mg: 18 } } },
  MedicationAtom: { category: 'medication', template: { name: '', dose: '0 mg', frequency: 'daily' } },
  ClinicalObsAtom:{ category: 'generic',  template: { atom_type: 'ClinicalObsAtom', payload: { finding: '', provider: 'self', date: '' } } },
  ContextAtom:    { category: 'context',  template: { note: '', category: 'general' } },
}

export default function Ingest() {
  const qc = useQueryClient()
  const [atomType, setAtomType] = useState('SleepAtom')
  const [payloadStr, setPayloadStr] = useState(
    JSON.stringify(ATOM_CONFIG['SleepAtom'].template, null, 2)
  )
  const [recordedAt, setRecordedAt] = useState(new Date().toISOString().slice(0, 16))
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const ingest = useMutation({
    mutationFn: async (raw: Record<string, unknown>) => {
      const cfg = ATOM_CONFIG[atomType]
      switch (cfg.category) {
        case 'wearable':
          return ingestWearable({ atom_type: atomType, payload: raw as Record<string, unknown>, recorded_at: new Date(recordedAt).toISOString() })
        case 'lab':
          return ingestLab({ ...raw as Parameters<typeof ingestLab>[0], recorded_at: new Date(recordedAt).toISOString() })
        case 'symptom':
          return ingestSymptom(raw as Parameters<typeof ingestSymptom>[0])
        case 'medication':
          return ingestMedication(raw as Parameters<typeof ingestMedication>[0])
        case 'context':
          return ingestContext({ ...raw as Parameters<typeof ingestContext>[0], date: new Date(recordedAt).toISOString() })
        default:
          return ingestAtom({ atom_type: atomType, payload: raw, data_source: 'manual', recorded_at: new Date(recordedAt).toISOString() })
      }
    },
    onSuccess: () => {
      setSuccess('Atom ingested successfully.')
      setError('')
      qc.invalidateQueries({ queryKey: ['timeline-summary'] })
      qc.invalidateQueries({ queryKey: ['timeline'] })
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(msg || 'Failed to ingest atom')
      setSuccess('')
    },
  })

  const handleTypeChange = (t: string) => {
    setAtomType(t)
    setPayloadStr(JSON.stringify(ATOM_CONFIG[t]?.template ?? {}, null, 2))
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    let parsed: Record<string, unknown>
    try {
      parsed = JSON.parse(payloadStr)
    } catch {
      setError('Payload is not valid JSON')
      return
    }
    ingest.mutate(parsed)
  }

  const cfg = ATOM_CONFIG[atomType]

  return (
    <div className="p-4 sm:p-6 max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold text-gray-900 mb-1">Add Health Data</h2>
      <p className="text-sm text-gray-500 mb-5 sm:mb-6">Ingest a typed atom into your personal atomspace</p>

      <form onSubmit={submit} className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 shadow-sm space-y-4">
        <div>
          <label className="text-sm font-medium text-gray-700 block mb-1">Atom type</label>
          <div className="flex items-center gap-3">
            <select
              value={atomType}
              onChange={(e) => handleTypeChange(e.target.value)}
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            >
              {Object.keys(ATOM_CONFIG).map((t) => (
                <option key={t} value={t}>{t.replace('Atom', '')}</option>
              ))}
            </select>
            <span className="text-xs px-2 py-1 bg-gray-100 text-gray-500 rounded">
              {cfg?.category}
            </span>
          </div>
        </div>

        {cfg?.category !== 'symptom' && cfg?.category !== 'medication' && (
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Recorded at</label>
            <input
              type="datetime-local"
              value={recordedAt}
              onChange={(e) => setRecordedAt(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
        )}

        <div>
          <label className="text-sm font-medium text-gray-700 block mb-1">
            Payload (JSON)
          </label>
          <textarea
            rows={6}
            value={payloadStr}
            onChange={(e) => setPayloadStr(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}
        {success && <p className="text-sm text-green-600">{success}</p>}

        <button
          type="submit"
          disabled={ingest.isPending}
          className="w-full bg-brand-600 text-white py-2 rounded-lg font-medium text-sm hover:bg-brand-700 disabled:opacity-50 transition-colors"
        >
          {ingest.isPending ? 'Ingesting…' : 'Ingest atom'}
        </button>
      </form>
    </div>
  )
}
